import React, { useState, useEffect } from 'react';
import { FiFilter, FiSearch, FiMapPin, FiRefreshCcw, FiCheckCircle, FiXCircle, FiEye } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { ClipLoader } from 'react-spinners';
import BaseUrl from '../../../Url/BaseUrl';
import {
  setSearchParams as setSearchParamsAction,
  setSearchResults,
  setFilter as setFilterAction,
  setSearchTerm as setSearchTermAction,
  resetSearch,
} from '../../../redux/Slices/busRoutesSlice';

const BusRoutesManage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // ---- persisted search state from redux ----
  const {
    searchParams,
    busRoutes,
    groupedRoutes,
    busRoutesMap,
    arrivalData,
    fullApiResponse,
    filter,
    searchTerm,
    hasSearched,
  } = useSelector((state) => state.busRoutes);

  // ---- local (non-persisted) UI state ----
  const [loading, setLoading] = useState(false);
  const [villagesLoading, setVillagesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [villages, setVillages] = useState([]);
  const [notification, setNotification] = useState({ type: '', message: '' });
  const [updatingStatus, setUpdatingStatus] = useState(null);

  const API_BASE_URL = BaseUrl.baseurl;

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification({ type: '', message: '' }), 3000);
  };

  useEffect(() => {
    fetchVillages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-group when busRoutes or busRoutesMap changes
  useEffect(() => {
    if (busRoutes.length > 0) {
      const grouped = groupRoutesByPath(busRoutes);
      dispatch(
        setSearchResults({
          busRoutes,
          groupedRoutes: grouped,
          busRoutesMap,
          arrivalData,
          fullApiResponse,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busRoutes, busRoutesMap]);

  const groupRoutesByPath = (routes) => {
    const groupedMap = new Map();

    routes.forEach((route) => {
      const routePathNo = route.routePathNo;
      const routeNo = route.routePath?.routeNo;
      const busRouteName =
        busRoutesMap[routeNo] ||
        route.routePath?.busRoute?.routeName ||
        'N/A';

      if (!groupedMap.has(routePathNo)) {
        groupedMap.set(routePathNo, {
          routePathNo,
          routePathName: route.routePath?.routePathName || 'Unnamed Route',
          busRouteName,
          status: route.status,
          villages: [],
          routePath: route.routePath,
        });
      }

      groupedMap.get(routePathNo).villages.push({
        villageCode: route.cityVillageCode,
        villageName: getVillageName(route.cityVillageCode),
        routeVillagesPathNo: route.routeVillagesPathNo,
        order: route.order || route.sequence || 0,
      });
    });

    groupedMap.forEach((group) => {
      group.villages.sort((a, b) => (a.order || 0) - (b.order || 0));
    });

    return Array.from(groupedMap.values());
  };

  const fetchVillages = async () => {
    setVillagesLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}CityVillages`);
      const sortedVillages = response.data.sort((a, b) =>
        a.cityVillageName.localeCompare(b.cityVillageName)
      );
      setVillages(sortedVillages);
    } catch (err) {
      setError('Failed to fetch villages. Please refresh the page.');
      console.error('Error fetching villages:', err);
      showNotification('error', 'Failed to load villages data');
    } finally {
      setVillagesLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchParams.fromCityVillageCode || !searchParams.toCityVillageCode) {
      alert('Please select both From and To villages');
      return;
    }

    if (searchParams.fromCityVillageCode === searchParams.toCityVillageCode) {
      alert('From and To villages cannot be the same');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_BASE_URL}RoutePathVillages/RoutePathVillageSearch`,
        searchParams,
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (
        response.data &&
        response.data.cityVillages &&
        response.data.cityVillages.length > 0
      ) {
        const routesData = response.data.cityVillages;

        // Fetch bus route names for unique routeNo values
        const uniqueRouteNos = [
          ...new Set(
            routesData.map((item) => item.routePath?.routeNo).filter(Boolean)
          ),
        ];

        let newMap = {};
        if (uniqueRouteNos.length > 0) {
          const fetchPromises = uniqueRouteNos.map((routeNo) =>
            axios.get(`${API_BASE_URL}BusRoutes/${routeNo}`).catch((err) => {
              console.error(`Failed to fetch bus route ${routeNo}:`, err);
              return null;
            })
          );
          const results = await Promise.all(fetchPromises);
          results.forEach((res, idx) => {
            newMap[uniqueRouteNos[idx]] =
              res && res.data ? res.data.routeName : 'Unknown Route';
          });
        }

        const grouped = groupRoutesByPath(routesData);

        dispatch(
          setSearchResults({
            busRoutes: routesData,
            groupedRoutes: grouped,
            busRoutesMap: newMap,
            arrivalData: response.data.arrival || [],
            fullApiResponse: response.data,
          })
        );

        showNotification('success', `Found ${routesData.length} route segments`);
      } else {
        dispatch(
          setSearchResults({
            busRoutes: [],
            groupedRoutes: [],
            busRoutesMap: {},
            arrivalData: [],
            fullApiResponse: response.data,
          })
        );
        setError('No routes found between the selected villages');
        showNotification('info', 'No routes found between the selected villages');
      }
    } catch (err) {
      setError('Failed to search routes. Please try again.');
      console.error('Error searching routes:', err);
      showNotification('error', 'Failed to search routes');
    } finally {
      setLoading(false);
    }
  };

  const handleResetSearch = () => {
    dispatch(resetSearch());
    setError(null);
  };

  // ⬇⬇⬇ NEW: clear persisted state, THEN navigate back
  const handleBack = () => {
    dispatch(resetSearch());
    navigate(-1);
  };

  const getVillageName = (code) => {
    const village = villages.find((v) => v.cityVillageCode === code);
    return village ? village.cityVillageName : 'Unknown Village';
  };

  const getRoutePathDisplay = (villagesList) => {
    if (!villagesList || villagesList.length === 0) return 'No villages';
    if (villagesList.length === 1) return villagesList[0].villageName;
    return `${villagesList[0].villageName} → ${
      villagesList[villagesList.length - 1].villageName
    }`;
  };

  const filteredRoutes = groupedRoutes.filter((route) => {
    const matchesFilter =
      filter === 'all' ||
      (filter === 'active' && route.status === true) ||
      (filter === 'inactive' && route.status === false);

    const matchesSearch =
      searchTerm === '' ||
      route.routePathNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      route.routePathName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      route.busRouteName?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesFilter && matchesSearch;
  });

  const toggleStatus = async (route) => {
    setUpdatingStatus(route.routePathNo);
    const routeSegments = busRoutes.filter(
      (r) => r.routePathNo === route.routePathNo
    );

    try {
      const togglePromises = routeSegments.map((segment) =>
        axios.get(
          `${API_BASE_URL}RoutePathVillages/ApprovalStatus?Id=${segment.routeVillagesPathNo}`
        )
      );
      await Promise.all(togglePromises);

      const newStatus = !route.status;

      dispatch(
        setSearchResults({
          busRoutes: busRoutes.map((r) =>
            r.routePathNo === route.routePathNo ? { ...r, status: newStatus } : r
          ),
          groupedRoutes: groupedRoutes.map((r) =>
            r.routePathNo === route.routePathNo ? { ...r, status: newStatus } : r
          ),
          busRoutesMap,
          arrivalData,
          fullApiResponse,
        })
      );

      showNotification(
        'success',
        `Route ${newStatus ? 'activated' : 'deactivated'} successfully!`
      );
    } catch (error) {
      console.error('Error toggling route status:', error);
      showNotification('error', 'Error updating route status. Please try again.');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleViewDetails = (route) => {
    const routeArrivalData = arrivalData.filter(
      (item) =>
        item.busStand?.cityVillage?.cityVillageCode &&
        route.villages.some(
          (v) => v.villageCode === item.busStand.cityVillage.cityVillageCode
        )
    );

    navigate(`/route-finder/routes/view/${route.routePathNo}`, {
      state: {
        route,
        villages,
        busRoutesMap,
        arrivalData,
        routeArrivalData,
        fullApiResponse,
        searchParams,
      },
    });
  };

  return (
    <div className="management-container">
      <div className="management-header">
        {/* ⬇ Changed from navigate(-1) to handleBack */}
        <button className="btn-back" onClick={handleBack}>
          ← Back
        </button>
        <h2>Manage Bus Routes</h2>
        <p>Search and manage bus routes between villages</p>
      </div>

      {notification.message && (
        <div className={`alert alert-${notification.type}`}>
          <span>{notification.message}</span>
          <button onClick={() => setNotification({ type: '', message: '' })}>×</button>
        </div>
      )}

      {/* Search Section */}
      <div className="village-search-section">
        <h3>Search Routes Between Villages</h3>
        <div className="village-search-form">
          <div className="form-group">
            <label htmlFor="fromVillage">From Village</label>
            <select
              id="fromVillage"
              value={searchParams.fromCityVillageCode}
              onChange={(e) =>
                dispatch(
                  setSearchParamsAction({
                    ...searchParams,
                    fromCityVillageCode: e.target.value,
                  })
                )
              }
              className="form-input"
              disabled={villagesLoading}
            >
              <option value="">Select From Village</option>
              {villages.map((village) => (
                <option
                  key={village.cityVillageCode}
                  value={village.cityVillageCode}
                >
                  {village.cityVillageName} ({village.cityVillageCode})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="toVillage">To Village</label>
            <select
              id="toVillage"
              value={searchParams.toCityVillageCode}
              onChange={(e) =>
                dispatch(
                  setSearchParamsAction({
                    ...searchParams,
                    toCityVillageCode: e.target.value,
                  })
                )
              }
              className="form-input"
              disabled={villagesLoading}
            >
              <option value="">Select To Village</option>
              {villages.map((village) => (
                <option
                  key={village.cityVillageCode}
                  value={village.cityVillageCode}
                >
                  {village.cityVillageName} ({village.cityVillageCode})
                </option>
              ))}
            </select>
          </div>

          <div className="form-actions">
            <button
              className="btn-primary"
              onClick={handleSearch}
              disabled={
                loading ||
                !searchParams.fromCityVillageCode ||
                !searchParams.toCityVillageCode
              }
            >
              {loading ? (
                <>
                  <ClipLoader size={16} color="#fff" /> Searching...
                </>
              ) : (
                'Search Routes'
              )}
            </button>
            <button
              className="btn-secondary"
              onClick={handleResetSearch}
              disabled={loading}
            >
              <FiRefreshCcw /> Reset
            </button>
          </div>
        </div>
      </div>

      {/* Filter and Search Toolbar - only when results exist */}
      {groupedRoutes.length > 0 && (
        <div className="management-toolbar">
          <div className="search-box">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by route number, name, or bus route..."
              value={searchTerm}
              onChange={(e) => dispatch(setSearchTermAction(e.target.value))}
              className="search-input"
            />
          </div>
          <div className="filter-controls">
            <div className="filter-group">
              <FiFilter className="filter-icon" />
              <select
                value={filter}
                onChange={(e) => dispatch(setFilterAction(e.target.value))}
                className="filter-select"
              >
                <option value="all">All Routes</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <button
              className="btn-primary"
              onClick={() => navigate('/route-finder/routes/add')}
            >
              + Add New Route
            </button>
          </div>
        </div>
      )}

      {/* Loading / Error States */}
      {loading && (
        <div className="loading-state">
          <ClipLoader size={40} color="#00BCD4" />
          <p>Searching for routes...</p>
        </div>
      )}

      {error && (
        <div className="error-state">
          <p className="error-message">{error}</p>
          {groupedRoutes.length === 0 && (
            <button onClick={handleResetSearch} className="btn-secondary">
              Try Another Search
            </button>
          )}
        </div>
      )}

      {/* Results Table */}
      {!loading && !error && groupedRoutes.length > 0 && (
        <>
          <div className="search-results-info">
            <p>
              Showing routes from{' '}
              <strong>{getVillageName(searchParams.fromCityVillageCode)}</strong>
              {' to '}
              <strong>{getVillageName(searchParams.toCityVillageCode)}</strong>{' '}
              ({filteredRoutes.length} unique routes found)
            </p>
          </div>

          <div className="routes-table-container">
            <table className="routes-table">
              <thead>
                <tr>
                  <th>Route Path No</th>
                  <th>Route Path Name</th>
                  <th>Route (First → Last)</th>
                  <th>Bus Route Name</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRoutes.map((route) => (
                  <tr key={route.routePathNo}>
                    <td>
                      <span className="route-id">{route.routePathNo}</span>
                    </td>
                    <td>
                      <div className="route-name-cell">
                        <FiMapPin className="route-icon" />
                        <span>{route.routePathName}</span>
                      </div>
                    </td>
                    <td>
                      <div className="villages-display">
                        {getRoutePathDisplay(route.villages)}
                      </div>
                    </td>
                    <td>{route.busRouteName}</td>
                    <td>
                      <button
                        className={`status-toggle ${
                          route.status ? 'active' : 'inactive'
                        }`}
                        onClick={() => toggleStatus(route)}
                        disabled={updatingStatus === route.routePathNo}
                        title={
                          route.status ? 'Click to deactivate' : 'Click to activate'
                        }
                      >
                        {updatingStatus === route.routePathNo ? (
                          <ClipLoader
                            size={12}
                            color={route.status ? '#155724' : '#721c24'}
                          />
                        ) : route.status ? (
                          <>
                            <FiCheckCircle /> Active
                          </>
                        ) : (
                          <>
                            <FiXCircle /> Inactive
                          </>
                        )}
                      </button>
                    </td>
                    <td>
                      <button
                        className="btn-view-details"
                        onClick={() => handleViewDetails(route)}
                        title="View route details"
                      >
                        <FiEye /> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredRoutes.length === 0 && (
            <div className="empty-state">
              <div className="empty-state-icon">🚌</div>
              <h3>No matching routes found</h3>
              <p>Try adjusting your search criteria</p>
            </div>
          )}
        </>
      )}

      {/* Initial State - only show if user has NEVER searched */}
      {!loading &&
        !error &&
        groupedRoutes.length === 0 &&
        !hasSearched && (
          <div className="empty-state">
            <div className="empty-state-icon">📍</div>
            <h3>Search for Routes</h3>
            <p>Select From and To villages to search for available routes</p>
          </div>
        )}

      <style jsx>{`
        .management-container {
          padding: 2rem;
          max-width: 1400px;
          margin: 0 auto;
          position: relative;
        }

        .management-header {
          margin-bottom: 2rem;
        }

        .management-header h2 {
          font-size: 2rem;
          margin-bottom: 0.5rem;
          color: #333;
        }

        .management-header p {
          color: #666;
          font-size: 1rem;
        }

        .alert {
          padding: 1rem 1.5rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          animation: slideIn 0.3s ease-out;
        }

        .alert-success {
          background: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }

        .alert-error {
          background: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }

        .alert-info {
          background: #d1ecf1;
          color: #0c5460;
          border: 1px solid #bee5eb;
        }

        .alert button {
          background: none;
          border: none;
          font-size: 1.2rem;
          cursor: pointer;
          color: inherit;
        }

        .village-search-section {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          padding: 1.5rem;
          margin-bottom: 2rem;
        }

        .village-search-section h3 {
          margin: 0 0 1.5rem 0;
          color: #333;
          font-size: 1.2rem;
        }

        .village-search-form {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-group label {
          font-weight: 600;
          font-size: 0.9rem;
          color: #495057;
        }

        .form-input {
          padding: 0.75rem;
          border: 1px solid #dee2e6;
          border-radius: 6px;
          font-size: 0.95rem;
          transition: border-color 0.2s;
          width: 100%;
          box-sizing: border-box;
        }

        .form-input:focus {
          outline: none;
          border-color: #00bcd4;
          box-shadow: 0 0 0 3px rgba(0, 188, 212, 0.1);
        }

        .form-group small {
          font-size: 0.8rem;
          color: #6c757d;
        }

        .form-actions {
          display: flex;
          gap: 1rem;
          align-items: flex-end;
        }

        .btn-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          transition: all 0.2s;
          min-height: 44px;
        }

        .btn-primary:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
        }

        .btn-primary:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .btn-secondary {
          background: #f8f9fa;
          color: #495057;
          border: 1px solid #dee2e6;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s;
          min-height: 44px;
        }

        .btn-secondary:hover:not(:disabled) {
          background: #e9ecef;
        }

        .btn-secondary:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .management-toolbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin: 2rem 0 1.5rem 0;
          gap: 1rem;
        }

        .search-box {
          display: flex;
          align-items: center;
          background: white;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          padding: 0.75rem 1rem;
          flex: 1;
          max-width: 400px;
        }

        .search-icon {
          color: #6c757d;
          margin-right: 0.5rem;
        }

        .search-input {
          border: none;
          outline: none;
          font-size: 0.95rem;
          width: 100%;
          background: transparent;
        }

        .filter-controls {
          display: flex;
          gap: 1rem;
          align-items: center;
        }

        .filter-group {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: white;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          padding: 0.5rem 0.75rem;
        }

        .filter-icon {
          color: #6c757d;
        }

        .filter-select {
          border: none;
          outline: none;
          background: transparent;
          font-size: 0.9rem;
          color: #495057;
          cursor: pointer;
        }

        .loading-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 3rem;
          color: #666;
        }

        .error-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          background: #f8d7da;
          border-radius: 8px;
          color: #721c24;
        }

        .error-message {
          margin-bottom: 1rem;
          text-align: center;
        }

        .search-results-info {
          background: #e3f2fd;
          color: #1565c0;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
        }

        .routes-table-container {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          overflow: hidden;
          margin-bottom: 2rem;
        }

        .routes-table {
          width: 100%;
          border-collapse: collapse;
        }

        .routes-table th {
          background: #f8f9fa;
          padding: 1rem;
          text-align: left;
          font-weight: 600;
          color: #495057;
          border-bottom: 2px solid #dee2e6;
          font-size: 0.9rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .routes-table td {
          padding: 1rem;
          border-bottom: 1px solid #eee;
          color: #212529;
          vertical-align: middle;
        }

        .routes-table tr:hover {
          background: #f8fafc;
        }

        .route-id {
          background: #e3f2fd;
          color: #1976d2;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
          font-family: 'Monaco', 'Courier New', monospace;
        }

        .route-name-cell {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .route-icon {
          color: #00bcd4;
        }

        .villages-display {
          font-size: 0.9rem;
        }

        .status-toggle {
          border: none;
          padding: 0.4rem 0.8rem;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.3rem;
          transition: all 0.2s;
        }

        .status-toggle.active {
          background: #d4edda;
          color: #155724;
        }

        .status-toggle.inactive {
          background: #f8d7da;
          color: #721c24;
        }

        .status-toggle:hover:not(:disabled) {
          transform: scale(1.05);
        }

        .status-toggle:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .btn-view-details {
          background: linear-gradient(135deg, #00bcd4 0%, #0097a7 100%);
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          transition: all 0.2s;
        }

        .btn-view-details:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 188, 212, 0.4);
        }

        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem;
          text-align: center;
          color: #666;
        }

        .empty-state-icon {
          font-size: 3em;
          margin-bottom: 1rem;
        }

        .empty-state h3 {
          margin: 0 0 0.5rem 0;
          color: #333;
        }

        @media (max-width: 1024px) {
          .management-container {
            padding: 1rem;
          }

          .routes-table-container {
            overflow-x: auto;
          }

          .routes-table {
            min-width: 1200px;
          }
        }

        @media (max-width: 768px) {
          .village-search-form {
            grid-template-columns: 1fr;
          }

          .form-actions {
            flex-direction: column;
          }

          .btn-primary,
          .btn-secondary {
            width: 100%;
            justify-content: center;
          }

          .management-toolbar {
            flex-direction: column;
            align-items: stretch;
          }

          .search-box {
            max-width: none;
          }

          .filter-controls {
            flex-direction: column;
            align-items: stretch;
          }

          .filter-group {
            justify-content: center;
          }
        }

        @keyframes slideIn {
          from {
            transform: translateY(-20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default BusRoutesManage;